import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSubmitQuoteRequest, useContractorProfile } from '@/api/hooks';
import { Footer } from './Footer';

interface QuoteRequestPageProps {
  userId: string;
}

export function QuoteRequestPage({ userId }: Readonly<QuoteRequestPageProps>) {
  const { t, i18n } = useTranslation();

  // Apply ?lang= URL parameter to set the page language
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const lang = params.get('lang');
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [i18n]);
  const submitRequest = useSubmitQuoteRequest();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const { data: profile, isLoading: profileLoading } = useContractorProfile(userId);
  const hasProfile = !!profile?.company_name;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t('intake.name');
    if (!description.trim()) newErrors.description = t('intake.description');
    if (!email.trim() && !phone.trim()) newErrors.contact = t('intake.contactRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    submitRequest.mutate(
      {
        userId,
        customer_name: name.trim(),
        customer_email: email.trim() || undefined,
        customer_phone: phone.trim() || undefined,
        project_description: description.trim(),
        address: address.trim() || undefined,
        _hp: honeypot || undefined,
      },
      {
        onSuccess: () => setSubmitted(true),
      },
    );
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-foreground mb-2">{t('intake.success')}</h1>
            <p className="text-muted-foreground">{t('intake.successDetail')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <form onSubmit={handleSubmit} className="w-full max-w-lg space-y-4">
          {profileLoading && (
            <div data-testid="profile-skeleton" className="mb-6 space-y-2">
              <div className="h-8 w-48 animate-pulse rounded bg-muted" />
              <div className="h-4 w-64 animate-pulse rounded bg-muted" />
            </div>
          )}
          {!profileLoading && hasProfile && (
            <div className="mb-6">
              {profile.logo_url && (
                <img
                  src={profile.logo_url}
                  alt={profile.company_name}
                  className="mb-3 max-w-[120px] rounded border border-border"
                />
              )}
              <h1 className="text-2xl font-bold text-foreground">{profile.company_name}</h1>
              {profile.tagline && (
                <p className="mt-1 text-sm text-muted-foreground">{profile.tagline}</p>
              )}
              {(profile.phone || profile.email) && (
                <div className="mt-2 flex gap-3 text-xs text-muted-foreground">
                  {profile.phone && <span>{profile.phone}</span>}
                  {profile.email && <span>{profile.email}</span>}
                </div>
              )}
            </div>
          )}
          {!profileLoading && !hasProfile && (
            <h1 className="text-2xl font-bold text-foreground mb-6">{t('intake.title')}</h1>
          )}

          {/* Honeypot - hidden from real users */}
          <div className="hidden">
            <Input
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
          </div>

          <div>
            <label htmlFor="intake-name" className="text-sm font-medium">
              {t('intake.name')} <span className="text-destructive">*</span>
            </label>
            <Input id="intake-name" value={name} onChange={(e) => setName(e.target.value)} />
            {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="intake-email" className="text-sm font-medium">
              {t('intake.email')}
            </label>
            <Input
              id="intake-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="intake-phone" className="text-sm font-medium">
              {t('intake.phone')}
            </label>
            <Input id="intake-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          {errors.contact && <p className="text-sm text-destructive">{errors.contact}</p>}

          <div>
            <label htmlFor="intake-description" className="text-sm font-medium">
              {t('intake.description')} <span className="text-destructive">*</span>
            </label>
            <textarea
              id="intake-description"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              rows={4}
              placeholder={t('intake.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            {errors.description && (
              <p className="text-sm text-destructive mt-1">{errors.description}</p>
            )}
          </div>

          <div>
            <label htmlFor="intake-address" className="text-sm font-medium">
              {t('intake.address')}
            </label>
            <Input
              id="intake-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          {submitRequest.isError && <p className="text-sm text-destructive">{t('intake.error')}</p>}

          <Button type="submit" className="w-full" disabled={submitRequest.isPending}>
            {t('intake.submit')}
          </Button>
        </form>
      </div>
      <Footer />
    </div>
  );
}
